import { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { Wallet as WalletIcon, Buildings, Briefcase } from "@phosphor-icons/react";

function Tile({ label, balance, credits, debits, Icon, tid, accent = "text-white" }) {
  return (
    <Card className="bg-[#141414] border-zinc-800 p-6">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">{label}</p>
        <Icon size={18} className="text-zinc-500" />
      </div>
      <p className={`mt-4 font-mono text-4xl ${accent}`} data-testid={TID.walletBalance(tid)}>{peso(balance)}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-mono text-zinc-500">
        <span>Credits: {peso(credits)}</span>
        <span>Debits: {peso(debits)}</span>
      </div>
    </Card>
  );
}

export default function Wallet() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    api.get("/wallets/summary").then(r => setSummary(r.data));
    api.get("/wallets/entries?limit=100").then(r => setEntries(r.data));
  }, []);

  if (!summary) return <div className="text-zinc-500">Loading…</div>;
  const isAdmin = ["super_admin","property_admin","finance_officer"].includes(user.role);

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Wallets</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter mt-1">
          {user.role === "owner" ? "Your earnings." : "Revenue split."}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Every guest payment splits per slot's revenue-share configuration.
        </p>
      </header>

      <div className="grid md:grid-cols-3 gap-4">
        <Tile label="Owner Wallet" tid="owner" balance={summary.owner.balance} credits={summary.owner.credits} debits={summary.owner.debits} Icon={WalletIcon} accent="text-emerald-400" />
        {isAdmin && <Tile label="Condo Wallet" tid="condo" balance={summary.condo.balance} credits={summary.condo.credits} debits={summary.condo.debits} Icon={Buildings} accent="text-blue-400" />}
        {isAdmin && <Tile label="Manager Wallet" tid="manager" balance={summary.manager.balance} credits={summary.manager.credits} debits={summary.manager.debits} Icon={Briefcase} accent="text-amber-300" />}
      </div>

      <Card className="bg-[#141414] border-zinc-800 p-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Recent Wallet Entries</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 text-xs font-mono uppercase">
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Wallet</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Description</th>
                <th className="py-2 pr-4 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {entries.map(e => (
                <tr key={e.id}>
                  <td className="py-2 pr-4 text-zinc-400 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="py-2 pr-4 capitalize">{e.wallet_type}</td>
                  <td className="py-2 pr-4 text-xs">{e.source}</td>
                  <td className="py-2 pr-4 text-zinc-400">{e.description}</td>
                  <td className="py-2 pr-4 text-right font-mono text-emerald-400">{peso(e.amount)}</td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-zinc-500">No wallet activity yet — confirm a guest payment first.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
